import React, {ReactElement, useCallback, useMemo} from 'react'
import {TFunction, useTranslation} from 'react-i18next'
import {AnswerGroupForm, BasicUser, ExtensionValidationRule, Language} from '../app/types'
import {useForm, useWatch} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import TextInput from '../common/form/TextInput'
import FormRow from '../common/form/FormRow'
import FormFieldBox from '../common/form/FormFieldBox'
import {useFormSubmission} from '../common/servercall/useFormSubmission'
import ErrorAlert from '../common/components/ErrorAlert'
import FormGroup from '../common/form/FormGroup'
import {useSelector} from 'react-redux'
import {
    allowedUserNcosSelector,
    disallowEnterpriseExtensionDialingSelector,
    extensionValidationRulesSelector,
    nrOfExtensionDigitsSelector,
    selectedSiteSelector
} from '../authentication/redux'
import {
    getAutocompleteContactCenterCodes,
    getAutocompleteSiteAnnouncements,
    getAutocompleteSiteAnnouncementsWithDefaultSelection,
    getAutocompleteSiteNumbers,
    getAutocompleteUsers,
    getAutocompleteUsersForProduct,
    regexForNumberWithoutEmergencyNr,
    regexForNumberWithoutEmergencyNrAndWithoutVM
} from '../common/util/helpers'
import FormContainer from '../common/form/FormContainer'
import {Box, Card, CardContent, CardHeader, Grid, Paper} from '@mui/material'
import {sort, split} from 'ramda'
import {formatUserInDropdown} from '../common/util/formatters'
import FormCheckbox from '../common/form/FormCheckbox'
import {emailRegex, extensionRegex} from "../users/UserForm";
import {useTableWithAdjustableOrder} from "../common/components/table/useTableWithAdjustableOrder";
import {ForwardToNumberField} from "../common/components/ForwardToNumberField";
import {usePhoneNumbersToForwardTo} from "../common/servercall/usePhoneNumbersToForwardTo";
import SupervisorSelection from "./premium/SupervisorSelection";
import SkilledAgentsSelection from "./premium/SkilledAgentsSelection";
import useExtensionUpdateChecker from "../common/extensionUpdateChecker";

const schema = (t: TFunction, nrOfExtensionDigits: number, extensionValidationRules: ExtensionValidationRule[]) =>
    yup
        .object()
        .shape({
            name: yup.string().trim().max(30, 'too-long').required('required-m'),
            language: yup.string().required('required-f'),
            extension: yup.lazy(value => {
                let schema = yup
                    .string()
                    .trim()
                    .required('required-f')
                    .matches(extensionRegex(nrOfExtensionDigits), {
                        message : t("general.form-error.invalid-extension", {nrOfExtensionDigits}),
                        excludeEmptyString: true
                    })
                extensionValidationRules.forEach(rule => {
                    schema = schema.test({
                        name: 'test',
                        test: value => !value || !new RegExp(`^${rule.regex}$`).test(value),
                        message: rule.errorKey,
                    })
                })
                return schema.nullable()
            }),
            number: yup
                .string()
                .matches(/^$|^\+32[0-9]{8}$|^0[0-9]{8}$/, 'invalid-phone-number')
                .nullable(),
            ncos: yup.string().required('required-f'),
            overflowNumber: yup
                .string()
                .when(['overflowDestination'], {
                    is: (overflowDestination: string) => overflowDestination === 'CALL_FORWARD',
                    then: schema => yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNrAndWithoutVM, {
                        message: 'invalid-phone-number',
                        excludeEmptyString: true,
                    }).required('required-m').nullable(),
                    otherwise: schema => yup.string().trim().nullable(),
                })
            ,
            overflowTimeout: yup
                .number()
                .when(['answerGroupType'], {
                    is: (answerGroupType: string) => answerGroupType === 'QUEUE',
                    then: schema => yup.number().required('required-f').typeError('invalid-overflow-timeout').min(5, 'invalid-overflow-timeout').max(600, 'invalid-overflow-timeout'),
                    otherwise: schema => yup.number().notRequired().nullable(),
                })
                .nullable(),
            forwardTimeoutSeconds: yup
                .number()
                .when(['answerGroupType'], {
                    is: (answerGroupType: string) => answerGroupType !== 'QUEUE',
                    then: schema => yup.number().required('required-f').typeError('invalid-overflow-timeout').min(5, 'invalid-overflow-timeout').max(600, 'invalid-overflow-timeout'),
                    otherwise: schema => yup.number().notRequired().nullable(),
                })
                .nullable(),
            overflowAnnouncementFileName: yup
                .string()
                .when(['overflowDestination'], {
                    is: (overflowDestination: string) => overflowDestination === 'BUSY_TREATMENT',
                    then: schema => yup.string().required('required-f').nullable(),
                    otherwise: schema => yup.string().nullable(),
                })
                .nullable(),
            voicemailEmail: yup
                .string()
                .when(['voicemailUsage'], {
                    is: (voicemailUsage: string) => ['FORWARD_TO_EMAIL', 'NOTIFY_BY_EMAIL'].includes(voicemailUsage),
                    then: schema => yup.string().trim().max(70, 'too-long').required('answergroup-email-required-when-voicemail').matches(
                        emailRegex,
                        'invalid-email'
                    ),
                    otherwise: schema => yup.string().trim().max(70, 'too-long').matches(
                        emailRegex,
                        'invalid-email'
                    ).nullable(),
                })
                .matches(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, {
                    message: 'invalid-email',
                    excludeEmptyString: true,
                }),
            additionalVoicemailEmails: yup.string().trim().max(400, 'too-long')
                .matches(/^((([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))(\s*;\s*|\s*$))*$/, {
                    message: 'invalid-email',
                    excludeEmptyString: true,
                }).nullable(),
            agentRingTime: yup.number().required('required-m'),
            huntPolicy: yup.string().required('required-m'),
            userAgents: yup.array().when(['answerGroupType', 'premiumCallCenter'], {
                is: (answerGroupType: string, premiumCallCenter: boolean) => answerGroupType === 'QUEUE' && !premiumCallCenter,
                then: schema => yup.array().min(1, 'agent-min-1-required'),
                otherwise: schema => yup.array(),
            }),
            welcomeMessageFileName: yup.string().nullable(),
            musicOnHoldFileName: yup.string().nullable(),
            comfortMessageFileName: yup.string().nullable(),
            comfortMessageGapTime: yup.number().typeError('invalid-frequency-comfort-message').min(10, 'invalid-frequency-comfort-message').max(999, 'invalid-frequency-comfort-message').nullable(),
            positionInQueueMessageInterval: yup.number().when(['positionInQueueMessageEnabled'], {
                is: (positionInQueueMessageEnabled: boolean) => positionInQueueMessageEnabled,
                then: schema => yup.number().required('required-f').typeError('invalid-frequency-position-in-queue-message').min(10, 'invalid-frequency-position-in-queue-message').max(600, 'invalid-frequency-position-in-queue-message').nullable(),
                otherwise: schema => yup.string().nullable(),
            }),
            maxWrapUpTimeAfterCall: yup.number().when(['wrapupTimer'], {
                is: (wrapupTimer: boolean) => wrapupTimer,
                then: schema =>   yup.number().required('required-f').typeError('invalid-max-wrap-up-time').min(0, 'invalid-max-wrap-up-time').max(300, 'invalid-max-wrap-up-time').nullable(),
                otherwise: schema => yup.string().nullable(),
            }),
            defaultCDCId: yup.string().when(['cdcstate'], {
                is: (cdcstate: string) => cdcstate === 'FORCE',
                then: schema =>   yup.string().required('required-m').nullable(),
                otherwise: schema => yup.string().nullable(),
            }),
            agentsSignedOutStrandedCallTransferNumber: yup
                .string()
                .when(['agentsSignedOutStrandedCallAction'], {
                    is: (destination: string) => destination === 'TRANSFER_TO_NUMBER',
                    then: schema => yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
                        message: 'invalid-phone-number',
                        excludeEmptyString: true,
                    }).required('required-m').nullable(),
                    otherwise: schema => yup.string().trim().nullable(),
                }),
            agentsUnavailableStrandedCallTransferNumber: yup
                .string()
                .when(['agentsUnavailableStrandedCallAction'], {
                    is: (destination: string) => destination === 'TRANSFER_TO_NUMBER',
                    then: schema => yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
                        message: 'invalid-phone-number',
                        excludeEmptyString: true,
                    }).required('required-m').nullable(),
                    otherwise: schema => yup.string().trim().nullable(),
                }),
            agentsSignedOutStrandedCallAnnouncement: yup
                .string()
                .when(['agentsSignedOutStrandedCallAction'], {
                    is: (overflowDestination: string) => overflowDestination === 'PLAY_ANNOUNCEMENT',
                    then: schema => yup.string().required('required-f').nullable(),
                    otherwise: schema => yup.string().nullable(),
                })
                .nullable(),
            agentsUnavailableStrandedCallAnnouncement: yup
                .string()
                .when(['agentsUnavailableStrandedCallAction'], {
                    is: (overflowDestination: string) => overflowDestination === 'PLAY_ANNOUNCEMENT',
                    then: schema => yup.string().required('required-f').nullable(),
                    otherwise: schema => yup.string().nullable(),
                })
                .nullable(),
            forcedForwardPhoneNumber: yup.string()
                .when(['premiumCallCenter'], {
                    is: (premiumCallCenter: boolean) => premiumCallCenter,
                    then: yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
                        message: 'invalid-phone-number',
                        excludeEmptyString: true,
                    }).nullable(),
                    otherwise: schema => yup.string().nullable(),
                }).nullable()
        })
        .required()

type Props = {
    flow: AnswerGroupForm
    onSubmit: (formData: AnswerGroupForm) => void
    readonly?: boolean
    buildFormButtons: (isSubmitting: boolean) => ReactElement
}

export const FlowForm = ({ flow, onSubmit, readonly, buildFormButtons }: Props) => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)
    const allowedUserNcos = useSelector(allowedUserNcosSelector)
    const extensionValidationRules = useSelector(extensionValidationRulesSelector)
    const nrOfExtensionDigits = useSelector(nrOfExtensionDigitsSelector)
    const disallowEnterpriseExtensionDialing = useSelector(disallowEnterpriseExtensionDialingSelector)
    const {phoneNumbersToForwardTo}= usePhoneNumbersToForwardTo(site)

    const { control, handleSubmit, setError, setValue, getFieldState } = useForm<AnswerGroupForm>({
        resolver: yupResolver<yup.AnyObjectSchema>(schema(t, nrOfExtensionDigits, extensionValidationRules)),
        defaultValues: {
            ...flow,
            wrapupTimer: flow.maxWrapUpTimeAfterCall !== null,
            positionInQueueMessageInterval: flow.positionInQueueMessageInterval == null ? 20 : flow.positionInQueueMessageInterval,
            defaultCDCId: flow.defaultCDC?.uuid || null,
            unavailableCodeId: flow.unavailableCode?.uuid || null,
        },
    })

    const userOptionParams = useMemo(() =>
        disallowEnterpriseExtensionDialing ?
            getAutocompleteUsers(
                t('flows.edit.no-users-found'),
                [],
                true,
                site) :
            getAutocompleteUsersForProduct(
                t('flows.edit.no-users-found'),
                [],
                site
            )
    , [site, t, disallowEnterpriseExtensionDialing])

    const { table: AgentTable, selector: AgentSelector } = useTableWithAdjustableOrder<BasicUser>('userAgents', userOptionParams, control, formatUserInDropdown, user => user.uuid, t('users.edit.user-select-button'))

    const { submit: submitAfterWarning, serverError } = useFormSubmission<AnswerGroupForm>(onSubmit, setError)

    const { Dialog : ExtensionUpdateWarningDialog , call: checkExtensionUpdate} = useExtensionUpdateChecker({url: `/api/site/${site?.productId}/answergroup/${flow.uuid}/check-extension-update`})

    const submitWithWarningCheck = useCallback(async (newFlow: AnswerGroupForm) => {
        async function doIt() {
            await submitAfterWarning(newFlow)
        }
        if (!flow.uuid) {
            await doIt()
        } else {
            await checkExtensionUpdate(newFlow.extension , doIt)
        }

    }, [flow, submitAfterWarning, checkExtensionUpdate])

    const { submit, isSubmitting, onError } = useFormSubmission<AnswerGroupForm>(submitWithWarningCheck, setError)

    const languageOptions = useMemo(
        () =>
            Object.values(Language).map(language => {
                return {
                    value: language,
                    label: t(`users.language.${language}`),
                }
            }),
        [t]
    )

    const numberOptionParams = useMemo(() => {
        return getAutocompleteSiteNumbers(t, false, site, flow?.number)
    }, [flow, site, t])

    const cdcCodes = useMemo(() => {
        return getAutocompleteContactCenterCodes(t, 'CDC', site, flow?.defaultCDC)
    }, [flow, site, t])

    const unavailableCodes = useMemo(() => {
        return getAutocompleteContactCenterCodes(t, 'AUC', site, flow?.unavailableCode)
    }, [flow, site, t])

    const ringTimePerAgentOptions = useMemo(
        () =>
            [6, 12, 18, 24, 30, 42, 60, 120].map(seconds => ({
                value: `${seconds}`,
                label: `${seconds} ${t('general.seconds-abbrev')}`,
            })),
        [t]
    )

    const queueLengthOptions = useMemo(() => {
        return Array.from({ length: 20 }, (_, index) => index + 1).map(seconds => ({
            value: seconds,
            label: `${seconds}`,
        }))
    }, [])

    const huntPolicyOptions = useMemo(
        () =>
            ['CIRCULAR', 'REGULAR', 'SIMULTANEOUS', 'UNIFORM'].map(policy => ({
                value: `${policy}`,
                label: `${t(`flows.edit.hunting-policy-names.${policy}`)}`,
            })),
        [t]
    )

    const agentStateAfterCallOption = useMemo(
        () =>
            ['NOT_SET', 'AVAILABLE', 'UNAVAILABLE', 'WRAP_UP'].map(state => ({
                value: `${state}`,
                label: `${t(`flows.edit.agent-state-after-call-options.${state}`)}`,
            })),
        [t]
    )

    const cdcStateOptions = useMemo(
        () =>
            ['DISABLED', 'ENABLED', 'FORCE'].map(state => ({
                value: `${state}`,
                label: `${t(`flows.edit.cdc-state-options.${state}`)}`,
            })),
        [t]
    )

    const voicemailUsageOptions = useMemo(
        () =>
            ['FORWARD_TO_EMAIL', 'NOTIFY_BY_EMAIL', 'WITHOUT_EMAIL'].map(usage => ({
                value: `${usage}`,
                label: `${t(`flows.edit.voicemail-usages.${usage}`)}`,
            })),
        [t]
    )

    const strandedCallActionOptions = useMemo(
        () =>
            ['NOTHING', 'TRANSFER_TO_NUMBER', 'NIGHT_SERVICE', 'PLAY_ANNOUNCEMENT'].map(action => ({
                value: `${action}`,
                label: `${t(`flows.edit.stranded-call-action-options.${action}`)}`,
            })),
        [t]
    )

    const overflowDestinationOptions = useMemo(
        () =>
            ['VOICEMAIL', 'CALL_FORWARD', 'BUSY_TREATMENT'].map(destination => ({
                value: `${destination}`,
                label: `${t(`flows.edit.overflow-destination-names.${destination}`)}`,
            })),
        [t]
    )

    const ringPatternOptions = useMemo(
        () =>
            (flow.answerGroupType === 'QUEUE' ? ['NORMAL', 'LONG_LONG', 'SHORT_SHORT_LONG', 'SHORT_LONG_SHORT'] : ['NORMAL', 'PRIO'])
                .map(destination => ({
                    value: `${destination}`,
                    label: `${t(`flows.ring-pattern-options.${destination}`)}`,
                })),
        [t, flow]
    )

    const overflowDestinationValue = useWatch({
        control,
        name: 'overflowDestination',
    })

    const voicemailUsageValue = useWatch({
        control,
        name: 'voicemailUsage',
    })

    const musicOnHoldFileName = useWatch({
        control,
        name: 'musicOnHoldFileName',
    })

    const agentStateAfterCall = useWatch({
        control,
        name: 'agentStateAfterCall',
    })

    const wrapuptimer = useWatch({
        control,
        name: 'wrapupTimer',
    })

    const playPositionInQueueMessageEnabled = useWatch({
        control,
        name: 'positionInQueueMessageEnabled',
    })

    const cdcState = useWatch({
        control,
        name: 'cdcstate',
    })

    const agentsSignedOutStrandedCallAction = useWatch({
        control,
        name: 'agentsSignedOutStrandedCallAction',
    })

    const agentsUnavailableStrandedCallAction = useWatch({
        control,
        name: 'agentsUnavailableStrandedCallAction',
    })

    const welcomeMessageOptionParams = useMemo(() => {
        return getAutocompleteSiteAnnouncements(t, 2000, site, flow.welcomeMessageFileName ?? undefined)
    }, [flow, site, t])

    const musicOnHoldMessageOptionParams = useMemo(() => {
        return getAutocompleteSiteAnnouncementsWithDefaultSelection(t, 5000, site, flow.musicOnHoldFileName ?? undefined)
    }, [flow, site, t])

    const voicemailNoAnswerAnnouncementOptionParams = useMemo(() => {
        return {
            ...getAutocompleteSiteAnnouncementsWithDefaultSelection(t, 2000, site, flow.voicemailNoAnswerAnnouncement ?? undefined),
            disableClearable: true,
        }
    }, [flow, site, t])

    const callForwardAnnouncementOptionParams = useMemo(() => {
        return getAutocompleteSiteAnnouncements(t, 2000, site, flow.callForwardAnnouncement ?? undefined)
    }, [flow, site, t])

    const strandedAnnouncementOptionParams = useMemo(() => {
        return getAutocompleteSiteAnnouncements(t, 2000, site, flow.agentsSignedOutStrandedCallAnnouncement ?? undefined)
    }, [flow, site, t])

    const strandedUnavailableAnnouncementOptionParams = useMemo(() => {
        return getAutocompleteSiteAnnouncements(t, 2000, site, flow.agentsUnavailableStrandedCallAnnouncement ?? undefined)
    }, [flow, site, t])

    const forcedForwardAnnouncementOptionParams = useMemo(() => {
        return getAutocompleteSiteAnnouncements(t, 2000, site, flow.forcedForwardAnnouncement ?? undefined)
    }, [flow, site, t])

    const comfortMessageOptionParams = useMemo(() => {
        return getAutocompleteSiteAnnouncements(t, 2000, site, flow.comfortMessageFileName ?? undefined)
    }, [flow, site, t])

    const overflowAnnouncementOptionParams = useMemo(() => {
        return getAutocompleteSiteAnnouncements(t, 2000, site, flow.overflowAnnouncementFileName ?? undefined)
    }, [flow, site, t])

    const ncosOptions = useMemo(
        () =>
            sort(
                (a, b) =>
                    a.localeCompare(b, undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    }),
                split(',', allowedUserNcos)
            )
                .filter(ncos => ncos !== 'NCOS99')
                .map(ncos => ({ label: t(`users.ncos-names.${ncos}`), value: ncos })),
        [t, allowedUserNcos]
    )

    return (
        <>
            <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError} />
            <ExtensionUpdateWarningDialog/>
            <FormContainer onSubmit={handleSubmit(submit, onError)}>
                <FormGroup label={t('flows.edit.section.info')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput readonly={readonly} autofocus={true} label={t('flows.edit.name')} name={'name'} control={control} required={true} />
                        </FormFieldBox>
                        <FormFieldBox>
                            <TextInput readonly={readonly} label={t('users.edit.language')} name={'language'} control={control} required={true} options={languageOptions} />
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput readonly={readonly} label={t('flows.edit.number')} name={'number'} control={control} required={false} autoCompleteOptions={numberOptionParams} />
                        </FormFieldBox>
                        <FormFieldBox>
                            <TextInput label={t('flows.edit.extension')} name={'extension'} control={control} required={true} />
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        {flow.answerGroupType === 'QUEUE' ? (

                                <FormFieldBox>
                                    <TextInput readonly={readonly} label={t('flows.edit.queue-length')} name={'queueLength'} control={control} required={false} options={queueLengthOptions} />
                                </FormFieldBox>

                        ) : (
                            <></>
                        )}
                    </FormRow>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput readonly={readonly} label={t('users.edit.ncos')} name={'ncos'} control={control} required={true} options={ncosOptions} />
                        </FormFieldBox>
                        <FormFieldBox>
                            <TextInput
                                readonly={readonly}
                                label={flow.answerGroupType === 'QUEUE' ? t('flows.edit.ring-pattern') : t('flows.edit.ring-pattern-for-external-calls')}
                                name={'ringPatternForExternalCalls'}
                                control={control}
                                required={true}
                                options={ringPatternOptions}
                                isRadio={false}
                            />
                        </FormFieldBox>
                    </FormRow>
                    {
                        flow.premiumCallCenter?
                            <Grid container mt={2}>
                                <Grid item xs={12} >
                                    <Card variant="outlined">
                                        <CardHeader subheader={t('flows.forced-forwarding')}/>
                                        <CardContent>
                                            <FormRow spacing={5}>
                                                <FormFieldBox>
                                                    <TextInput readonly={readonly} label={t('executives.screening-alerting.forward-to-number')} name={'forcedForwardPhoneNumber'} control={control} required={false} />
                                                </FormFieldBox>
                                                <FormFieldBox>
                                                    <TextInput
                                                        help={t('general.max-file-size', {size: 2})}
                                                        readonly={readonly}
                                                        label={t('announcements.announcement')}
                                                        name={'forcedForwardAnnouncement'}
                                                        control={control}
                                                        required={true}
                                                        autoCompleteOptions={forcedForwardAnnouncementOptionParams}
                                                    />
                                                </FormFieldBox>
                                            </FormRow>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid> : <></>
                    }
                </FormGroup>
                <FormGroup label={t('users.edit.section.privacy')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <FormCheckbox
                                label={t('users.edit.hide-from-phonebook')}
                                name={'hideFromPhoneBook'}
                                control={control}
                                setValue={setValue}
                                getFieldState={getFieldState}
                            />
                        </FormFieldBox>
                    </FormRow>
                </FormGroup>
                <FormGroup label={t('flows.edit.section.voicemail')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput readonly={readonly} label={t('flows.edit.voicemail-usage')} name={'voicemailUsage'} control={control} required={true} options={voicemailUsageOptions} />
                        </FormFieldBox>
                        <FormFieldBox>
                            <TextInput readonly={readonly} label={t('flows.edit.voicemail-email')} name={'voicemailEmail'} control={control} required={voicemailUsageValue !== 'WITHOUT_EMAIL'} />
                        </FormFieldBox>
                        {voicemailUsageValue === 'FORWARD_TO_EMAIL' &&
                            <FormFieldBox fullWidth={true}>
                                <TextInput help={t('flows.edit.additional-voicemail-emails-help')} readonly={readonly} label={t('flows.edit.additional-voicemail-emails')} name={'additionalVoicemailEmails'} control={control} />
                            </FormFieldBox>
                        }
                    </FormRow>
                </FormGroup>
                {
                    flow.answerGroupType === 'QUEUE' && flow.premiumCallCenter && !flow.uuid ?
                        <SupervisorSelection control={control} site={site}/> : null
                }
                {
                    flow.answerGroupType === 'QUEUE' && flow.premiumCallCenter ?
                        <SkilledAgentsSelection control={control} site={site}/> : null
                }
                <FormGroup label={t('flows.edit.section.agents')} fullWidth={true}>
                    {
                        !flow.premiumCallCenter &&
                            <>
                                <FormRow>
                                    <FormFieldBox mdSize={8} lgSize={6}>
                                        {AgentSelector}
                                    </FormFieldBox>
                                </FormRow>
                                <FormRow>
                                    <FormFieldBox mdSize={12}>
                                        <Paper elevation={2}>
                                            <AgentTable />
                                        </Paper>
                                    </FormFieldBox>
                                </FormRow>
                            </>
                    }
                    <Box width={1} mt={2}></Box>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput readonly={readonly} label={t('flows.edit.ring-time-per-agent')} name={'agentRingTime'} control={control} required={true} options={ringTimePerAgentOptions} />
                        </FormFieldBox>
                        <FormFieldBox>
                            <TextInput readonly={readonly} label={t('flows.edit.hunt-policy')} name={'huntPolicy'} control={control} required={true} options={huntPolicyOptions} />
                        </FormFieldBox>
                    </FormRow>

                    <FormRow>
                        {flow.answerGroupType === 'QUEUE' && (
                            <FormFieldBox>
                                <FormCheckbox label={t('flows.edit.allow-agent-logoff')} name={'allowAgentLogoff'} control={control} setValue={setValue} />
                            </FormFieldBox>
                        )}
                        <FormFieldBox>
                            <FormCheckbox label={t('flows.edit.allow-call-waiting-for-agents')} name={'allowCallWaitingForAgents'} control={control} setValue={setValue} />
                        </FormFieldBox>
                    </FormRow>

                    {flow.answerGroupType === 'QUEUE' && flow.premiumCallCenter && (
                        <FormRow>
                            <FormFieldBox>
                                <TextInput readonly={readonly} label={t('flows.edit.agent-state-after-call')} name={'agentStateAfterCall'} control={control} required={true} options={agentStateAfterCallOption} />
                            </FormFieldBox>
                        </FormRow>
                    )}
                    {agentStateAfterCall === 'WRAP_UP' && (
                        <FormRow>
                            <FormFieldBox>
                                <FormCheckbox label={t('flows.edit.wrap-up-timer')} name={'wrapupTimer'} control={control} setValue={setValue} />
                            </FormFieldBox>
                            {wrapuptimer && (
                                <FormFieldBox>
                                    <TextInput
                                        readonly={readonly}
                                        label={t('flows.edit.wrap-up-seconds')}
                                        name={'maxWrapUpTimeAfterCall'}
                                        control={control}
                                        required={true}
                                    />
                                </FormFieldBox>
                            )}
                        </FormRow>
                    )}
                    {flow.answerGroupType === 'QUEUE' && flow.premiumCallCenter && (
                        <FormRow>
                            <FormFieldBox>
                                <TextInput readonly={readonly} label={t('flows.edit.call-disposition-codes')} name={'cdcstate'} control={control} required={true} options={cdcStateOptions} />
                            </FormFieldBox>
                            {cdcState === 'FORCE' && (
                                <FormFieldBox>
                                    <TextInput readonly={readonly} label={t('flows.edit.default-cdc')} name={'defaultCDCId'} control={control} required={true} autoCompleteOptions={cdcCodes} />
                                </FormFieldBox>
                            )}
                        </FormRow>
                    )}
                </FormGroup>

                <FormGroup label={t('flows.edit.section.media')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput
                                help={t('general.max-file-size', {size: 2})}
                                readonly={readonly}
                                label={t('flows.edit.welcome-message')}
                                name={'welcomeMessageFileName'}
                                control={control}
                                required={false}
                                autoCompleteOptions={welcomeMessageOptionParams}
                            />
                        </FormFieldBox>
                    </FormRow>
                    {flow.answerGroupType === 'QUEUE' && (
                        <FormRow>
                            <FormFieldBox>
                                <TextInput
                                    help={t('general.max-file-size', {size: 5})}
                                    readonly={readonly}
                                    label={t('flows.edit.music-on-hold')}
                                    name={'musicOnHoldFileName'}
                                    control={control}
                                    required={false}
                                    autoCompleteOptions={musicOnHoldMessageOptionParams}
                                />
                            </FormFieldBox>
                            {!!musicOnHoldFileName && (<FormFieldBox>
                                <FormCheckbox
                                    label={t('flows.edit.play-ringing-when-offering-call')}
                                    name={'playRingingWhenOfferingCall'}
                                    control={control}
                                    setValue={setValue}
                                    getFieldState={getFieldState}
                                />
                            </FormFieldBox>)}
                        </FormRow>
                    )}
                    {flow.answerGroupType === 'QUEUE' && (
                        <FormRow>
                            <FormFieldBox>
                                <TextInput
                                    help={t('general.max-file-size', {size: 2})}
                                    readonly={readonly}
                                    label={t('flows.edit.comfort-media-file')}
                                    name={'comfortMessageFileName'}
                                    control={control}
                                    required={false}
                                    autoCompleteOptions={comfortMessageOptionParams}
                                />
                            </FormFieldBox>
                            <FormFieldBox>
                                <TextInput
                                    readonly={readonly}
                                    label={t('flows.edit.comfort-media-frequency')}
                                    name={'comfortMessageGapTime'}
                                    control={control}
                                    required={false}
                                />
                            </FormFieldBox>
                        </FormRow>
                    )}
                    {flow.answerGroupType === 'QUEUE' && flow.premiumCallCenter && (
                        <FormRow>
                            <FormFieldBox>
                                <FormCheckbox
                                    label={t('flows.edit.play-position-in-queue-message')}
                                    name={'positionInQueueMessageEnabled'}
                                    control={control}
                                    setValue={setValue}
                                    getFieldState={getFieldState}
                                />
                            </FormFieldBox>
                            {playPositionInQueueMessageEnabled && <FormFieldBox>
                                <TextInput
                                    readonly={readonly}
                                    label={t('flows.edit.play-position-in-queue-message-interval')}
                                    name={'positionInQueueMessageInterval'}
                                    control={control}
                                    required={true}
                                />
                            </FormFieldBox>}
                        </FormRow>
                    )}
                </FormGroup>

                <FormGroup label={t('flows.edit.section.overflow')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput
                                readonly={readonly}
                                label={t('flows.edit.overflow-destination')}
                                name={'overflowDestination'}
                                control={control}
                                required={false}
                                options={overflowDestinationOptions}
                                isRadio={true}
                            />
                        </FormFieldBox>
                        {flow.answerGroupType === 'QUEUE' ? (
                            <FormFieldBox>
                                <TextInput readonly={readonly} label={t('flows.edit.overflow-timeout')} name={'overflowTimeout'} control={control} required={true} />
                            </FormFieldBox>
                        ) : (
                            <FormFieldBox>
                                <TextInput readonly={readonly} label={t('flows.edit.forward-timeout')} name={'forwardTimeoutSeconds'} control={control} required={true} />
                            </FormFieldBox>
                        )}
                    </FormRow>
                    {overflowDestinationValue === 'VOICEMAIL' && (
                        <FormRow>
                            <FormFieldBox>
                                <TextInput
                                    help={t('general.max-file-size', {size: 2})}
                                    readonly={readonly}
                                    label={t('flows.edit.voicemail-greeting')}
                                    name={'voicemailNoAnswerAnnouncement'}
                                    control={control}
                                    required={true}
                                    autoCompleteOptions={voicemailNoAnswerAnnouncementOptionParams}
                                />
                            </FormFieldBox>
                        </FormRow>
                    )}
                    {overflowDestinationValue === 'CALL_FORWARD' && (
                        <FormRow>
                            <FormFieldBox>
                                <ForwardToNumberField
                                    phoneNumbersToForwardTo={phoneNumbersToForwardTo}
                                    defaultNumber={flow.overflowNumber || ''}
                                    readonly={readonly}
                                    required={true}
                                    label={t('flows.edit.overflow-number')}
                                    name={'overflowNumber'}
                                    control={control}
                                />
                            </FormFieldBox>
                            <FormFieldBox>
                                <TextInput
                                    help={t('general.max-file-size', {size: 2})}
                                    readonly={readonly}
                                    label={t('flows.edit.call-forward-announcement')}
                                    name={'callForwardAnnouncement'}
                                    control={control}
                                    required={false}
                                    autoCompleteOptions={callForwardAnnouncementOptionParams}
                                />
                            </FormFieldBox>
                        </FormRow>
                    )}
                    {overflowDestinationValue === 'BUSY_TREATMENT' && (
                        <FormRow>
                            <FormFieldBox>
                                <TextInput
                                    help={t('general.max-file-size', {size: 2})}
                                    readonly={readonly}
                                    label={t('flows.edit.overflow-destination-names.BUSY_TREATMENT')}
                                    name={'overflowAnnouncementFileName'}
                                    control={control}
                                    required={true}
                                    autoCompleteOptions={overflowAnnouncementOptionParams}
                                />
                            </FormFieldBox>
                        </FormRow>
                    )}
                </FormGroup>
                {
                    flow.answerGroupType === 'QUEUE' && flow.premiumCallCenter ?
                    <FormGroup label={t('flows.edit.section.stranded-calls')} fullWidth={true}>
                        <FormRow>
                            <FormFieldBox>
                                <TextInput
                                    readonly={readonly}
                                    label={t('flows.edit.stranded-call-action')}
                                    name={'agentsSignedOutStrandedCallAction'}
                                    control={control}
                                    required={false}
                                    options={strandedCallActionOptions}
                                />
                            </FormFieldBox>
                            {agentsSignedOutStrandedCallAction === 'TRANSFER_TO_NUMBER' && <FormFieldBox>
                                <ForwardToNumberField
                                    phoneNumbersToForwardTo={phoneNumbersToForwardTo}
                                    defaultNumber={flow.agentsSignedOutStrandedCallTransferNumber || ''}
                                    readonly={readonly}
                                    required={true}
                                    label={t('flows.edit.overflow-number')}
                                    name={'agentsSignedOutStrandedCallTransferNumber'}
                                    control={control}
                                />
                                </FormFieldBox>
                            }
                            {agentsSignedOutStrandedCallAction === 'PLAY_ANNOUNCEMENT' && <FormFieldBox>
                                <TextInput
                                    help={t('general.max-file-size', {size: 2})}
                                    readonly={readonly}
                                    label={t('announcements.announcement')}
                                    name={'agentsSignedOutStrandedCallAnnouncement'}
                                    control={control}
                                    required={true}
                                    autoCompleteOptions={strandedAnnouncementOptionParams}
                                />
                            </FormFieldBox>
                            }
                        </FormRow>
                        <FormRow>
                            <FormFieldBox>
                                <TextInput
                                    readonly={readonly}
                                    label={t('flows.edit.stranded-call-unavailable-action')}
                                    name={'agentsUnavailableStrandedCallAction'}
                                    control={control}
                                    required={false}
                                    options={strandedCallActionOptions}
                                />
                            </FormFieldBox>
                            {agentsUnavailableStrandedCallAction === 'TRANSFER_TO_NUMBER' && <FormFieldBox>
                                <ForwardToNumberField
                                    phoneNumbersToForwardTo={phoneNumbersToForwardTo}
                                    defaultNumber={flow.agentsUnavailableStrandedCallTransferNumber || ''}
                                    readonly={readonly}
                                    required={true}
                                    label={t('flows.edit.overflow-number')}
                                    name={'agentsUnavailableStrandedCallTransferNumber'}
                                    control={control}
                                />
                            </FormFieldBox>
                            }
                            {agentsUnavailableStrandedCallAction === 'PLAY_ANNOUNCEMENT' && <FormFieldBox>
                                <TextInput
                                    help={t('general.max-file-size', {size: 2})}
                                    readonly={readonly}
                                    label={t('announcements.announcement')}
                                    name={'agentsUnavailableStrandedCallAnnouncement'}
                                    control={control}
                                    required={true}
                                    autoCompleteOptions={strandedUnavailableAnnouncementOptionParams}
                                />
                            </FormFieldBox>
                            }
                        </FormRow>
                        <FormRow>
                            <FormFieldBox>
                                <TextInput readonly={readonly} label={t('flows.edit.unavailable-code')} name={'unavailableCodeId'} control={control} required={false} autoCompleteOptions={unavailableCodes} />
                            </FormFieldBox>
                        </FormRow>
                    </FormGroup> : null
                }
                {!readonly && buildFormButtons(isSubmitting)}
            </FormContainer>
        </>
    )
}
